import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

/** Library → Generate video (drafts on Higgsfield, 1080p finish on fal.ai). */

export type AiVideoResolution = "480p" | "720p";
export type AiVideoAspectRatio = "9:16" | "16:9" | "1:1";

export interface AiVideoModelOption {
  id: string;
  label: string;
  minSeconds: number;
  maxSeconds: number;
  resolutions: AiVideoResolution[];
  maxReferences: number;
  usdPerSecond: Partial<Record<AiVideoResolution, number>>;
}

export interface AiVideoOptions {
  models: AiVideoModelOption[];
  aspectRatios: AiVideoAspectRatio[];
  upscale: { resolution: string; usdPerSecond: number };
  defaults: { model: string; resolution: AiVideoResolution; aspectRatio: AiVideoAspectRatio; durationSeconds: number };
  configured: { generate: boolean; upscale: boolean };
}

export interface AiVideoJob {
  id: string;
  type: "video_draft" | "video_upscale";
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED";
  error: string | null;
  createdAt: string;
  prompt: string | null;
  model: string | null;
  aspectRatio: AiVideoAspectRatio | null;
  durationSeconds: number | null;
  resolution: AiVideoResolution | null;
  sourceAssetId: string | null;
  referenceAssetIds: string[];
}

export interface AiVideoDraftInput {
  prompt: string;
  model: string;
  aspectRatio: AiVideoAspectRatio;
  durationSeconds: number;
  resolution: AiVideoResolution;
  referenceAssetIds?: string[];
}

export const isActiveJob = (j: AiVideoJob) => j.status === "PENDING" || j.status === "RUNNING";

/** List price of a draft, rounded to the cent for display. */
export function estimateDraftUsd(model: AiVideoModelOption | undefined, resolution: AiVideoResolution, seconds: number): number {
  const rate = model?.usdPerSecond[resolution] ?? 0;
  return rate * seconds;
}

export function formatUsd(usd: number): string {
  return `$${usd < 0.1 ? usd.toFixed(3).replace(/0$/, "") : usd.toFixed(2)}`;
}

export function useAiVideoOptions(brandId: string) {
  return useQuery({
    queryKey: ["ai-video-options", brandId],
    queryFn: () => apiFetch<AiVideoOptions>(`/brands/${brandId}/ai-video/options`),
    enabled: !!brandId,
    staleTime: 5 * 60_000,
  });
}

/**
 * In-progress jobs and recent failures. Polls only while something is
 * rendering, and refreshes the Library as each job finishes so the new
 * asset shows up without a reload.
 */
export function useAiVideoJobs(brandId: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["ai-video-jobs", brandId],
    queryFn: () => apiFetch<AiVideoJob[]>(`/brands/${brandId}/ai-video/jobs`),
    enabled: !!brandId,
    refetchInterval: (q) => ((q.state.data ?? []).some(isActiveJob) ? 5_000 : false),
  });

  const activeIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = new Set((query.data ?? []).filter(isActiveJob).map((j) => j.id));
    const finished = [...activeIds.current].some((id) => !now.has(id));
    activeIds.current = now;
    if (finished) {
      qc.invalidateQueries({ queryKey: ["library-assets", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-assets", brandId] });
    }
  }, [query.data, qc, brandId]);

  return query;
}

export function useWriteVideoBrief(brandId: string) {
  return useMutation({
    mutationFn: (input: { idea: string; aspectRatio: AiVideoAspectRatio; durationSeconds: number }) =>
      apiFetch<{ prompt: string }>(`/brands/${brandId}/ai-video/brief`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  });
}

function useJobMutation<TInput>(brandId: string, path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<{ job: AiVideoJob; estimateUsd: number }>(`/brands/${brandId}/ai-video/${path}`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-video-jobs", brandId] }),
  });
}

export function useGenerateVideoDraft(brandId: string) {
  return useJobMutation<AiVideoDraftInput>(brandId, "drafts");
}

export function useUpscaleVideo(brandId: string) {
  return useJobMutation<{ sourceAssetId: string }>(brandId, "upscales");
}

export function useDismissAiVideoJob(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      apiFetch<{ ok: true }>(`/brands/${brandId}/ai-video/jobs/${jobId}/dismiss`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-video-jobs", brandId] }),
  });
}
